import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useWallet, useConnection } from "../../contexts/WalletContext";
import { useAuth } from "../../contexts/AuthContext";
import { LAMPORTS_PER_SOL, Transaction, TransactionInstruction, PublicKey, VersionedTransaction } from "@solana/web3.js";
import { executeBuy, executeSell, getOpenPositions, getTokenBalance, prefetchBuyData } from "../../services/trading-api";
import { getPrimaryWalletKeypair, getWalletKeypairByPublicKey } from "../../services/wallet-service";
import logger from "../../utils/logger";

const TradingControls = ({ tokenAddress, pairAddress, chainId, currentPrice, pair }) => {
  const { publicKey, sendTransaction, signTransaction, connected } = useWallet();
  const { connection } = useConnection();
  const { vault, vaultKey, isUnlocked, user } = useAuth();
  const [mode, setMode] = useState("buy"); // "buy" or "sell"
  const [amount, setAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [tokenBalance, setTokenBalance] = useState(0);
  const [tokenDecimals, setTokenDecimals] = useState(6);
  const [openPosition, setOpenPosition] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);
  
  
  // Get the wallet address to use: vault primary wallet OR connected Phantom wallet
  const walletAddress = useMemo(() => {
    if (isUnlocked && vault && vault.wallets && vault.wallets.length > 0) {
      // Use primary wallet from vault
      const primaryWallet = vault.wallets.find(w => w.isPrimary) || vault.wallets[0];
      const addr = primaryWallet.publicKey;
      logger.debug('WALLET', `Using vault wallet: ${addr.substring(0, 8)}...`);
      return addr;
    }
    // Fallback to Phantom wallet
    const phantomAddr = connected && publicKey ? publicKey.toString() : null;
    if (phantomAddr) {
      logger.debug('WALLET', `Using Phantom wallet: ${phantomAddr.substring(0, 8)}...`);
        } else {
      logger.warn('WALLET', 'No wallet available (vault not unlocked and Phantom not connected)');
    }
    return phantomAddr;
  }, [isUnlocked, vault, connected, publicKey]);
  const [buttonStatus, setButtonStatus] = useState(null); // "success", "failed", or null
  const [errorMessage, setErrorMessage] = useState(null);
  const [executionStage, setExecutionStage] = useState(null); // "placing" | "verifying" | "creating-position" | "success" | "failed"
  const executionTimeoutRef = useRef(null);
  const executionStageRef = useRef(null); // Track current stage for timeout checks
  
  // Wrapper to update both state and ref
  const updateExecutionStage = useCallback((stage) => {
    executionStageRef.current = stage;
    setExecutionStage(stage);
  }, []);
  
  // Helper function to verify transaction is confirmed on-chain
  const verifyTransactionOnChain = useCallback(async (signature, connection, maxWaitTime = 30000) => {
    const startTime = Date.now();
    const pollInterval = 1000; // Check every 1 second
    
    logger.info('VERIFICATION', 'Starting transaction verification', { signature: signature.substring(0, 8) + '...' });
    
    while (Date.now() - startTime < maxWaitTime) {
      try {
        // Check signature status
        const statuses = await connection.getSignatureStatuses([signature]);
        const status = statuses?.value?.[0];
        
        if (status) {
          // Transaction found
          if (status.err) {
            // Transaction failed
            logger.error('VERIFICATION', 'Transaction failed on-chain', { 
              signature: signature.substring(0, 8) + '...',
              error: status.err 
            });
            throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
          }
          
          if (status.confirmationStatus === 'confirmed' || status.confirmationStatus === 'finalized') {
            // Transaction confirmed!
            logger.info('VERIFICATION', 'Transaction confirmed', { 
              signature: signature.substring(0, 8) + '...',
              confirmationStatus: status.confirmationStatus 
            });
            return { success: true, confirmationStatus: status.confirmationStatus };
          }
          
          // Transaction exists but not confirmed yet - keep waiting
          logger.debug('VERIFICATION', 'Transaction pending confirmation', { 
            signature: signature.substring(0, 8) + '...',
            confirmationStatus: status.confirmationStatus 
          });
        }
        
        // Wait before next poll
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      } catch (error) {
        // If error is transaction failure, throw it
        if (error.message.includes('Transaction failed')) {
          throw error;
        }
        // Otherwise, continue polling
        logger.debug('VERIFICATION', 'Polling error (continuing)', { error: error.message });
        await new Promise(resolve => setTimeout(resolve, pollInterval));
      }
    }
    
    // Timeout - transaction not confirmed in time
    logger.error('VERIFICATION', 'Transaction confirmation timeout', { 
      signature: signature.substring(0, 8) + '...',
      maxWaitTime 
    });
    throw new Error('Transaction confirmation timeout');
  }, []);
  
  // Settings state (separate for buy and sell, persisted in localStorage)
  const [buySettings, setBuySettings] = useState(() => {
    const saved = localStorage.getItem('trading_buy_settings');
    return saved ? JSON.parse(saved) : { slippage: 20, priorityFee: 0.0001, tip: 0.0001 };
  });
  const [sellSettings, setSellSettings] = useState(() => {
    const saved = localStorage.getItem('trading_sell_settings');
    return saved ? JSON.parse(saved) : { slippage: 20, priorityFee: 0.0001, tip: 0.0001 };
  });
  const [showSettings, setShowSettings] = useState(false);
  
  // Track if we've made a buy on this page to know when to start fetching token balance
  const hasBoughtOnPageRef = useRef(false);
  
  // Refs to store interval IDs for resetting polling after actions
  const solBalanceIntervalRef = useRef(null);
  const tokenBalanceIntervalRef = useRef(null);
  const positionIntervalRef = useRef(null);
  
  // Prefetch cache for buy transaction data
  const prefetchCacheRef = useRef(null);
  const prefetchTimeoutRef = useRef(null);
  const isPrefetchingRef = useRef(false);
  
  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('trading_buy_settings', JSON.stringify(buySettings));
  }, [buySettings]);
  
  useEffect(() => {
    localStorage.setItem('trading_sell_settings', JSON.stringify(sellSettings));
  }, [sellSettings]);

  // Get token name/symbol from pair
  const tokenName = pair?.baseToken?.symbol || pair?.baseToken?.tokenSymbol || "Token";

  // Get wallet balance - fetch every 30 seconds, no loading UI
  // Use wallet address from vault or Phantom wallet
  useEffect(() => {
    const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    
    if (!balanceWalletAddress) {
      setBalance(0);
      return;
    }

    if (!connection) {
      setBalance(0);
      return;
    }

    const fetchBalance = async () => {
      const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
      if (!balanceWalletAddress) {
        logger.debug('BALANCE', 'Skipping SOL balance fetch - no wallet address');
        return;
      }
      
      const fetchStartTime = performance.now();
      try {
        const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
        logger.debug('API_CALL', `SOL balance fetch started`, { 
          wallet: balanceWalletAddress.substring(0, 8), 
          startTime: fetchStartTime 
        });
        const response = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
        const fetchEndTime = performance.now();
        const fetchDuration = fetchEndTime - fetchStartTime;
        
        if (response.ok) {
          const data = await response.json();
          const solBalance = data.balance || 0;
          const oldBalance = balance;
          setBalance(solBalance);
          logger.info('UI_STATE', `SOL balance updated (polling)`, { 
            wallet: balanceWalletAddress.substring(0, 8),
            oldBalance, 
            newBalance: solBalance,
            duration: fetchDuration,
            startTime: fetchStartTime,
            endTime: fetchEndTime
          });
        } else {
          logger.warn('API_CALL', `SOL balance fetch failed`, { 
            wallet: balanceWalletAddress.substring(0, 8),
            status: response.status, 
            statusText: response.statusText,
            duration: fetchDuration
          });
        }
      } catch (err) {
        const fetchEndTime = performance.now();
        logger.error('API_CALL', 'SOL balance fetch exception', { 
          wallet: balanceWalletAddress.substring(0, 8),
          error: err.message, 
          duration: fetchEndTime - fetchStartTime
        });
      }
    };

    fetchBalance();
    solBalanceIntervalRef.current = setInterval(fetchBalance, 5000); // Every 5 seconds (faster refresh)
    return () => {
      if (solBalanceIntervalRef.current) {
        clearInterval(solBalanceIntervalRef.current);
      }
    };
  }, [walletAddress, connected, publicKey, connection, isUnlocked, vault]);

  // Manual fetch function for token balance (memoized with useCallback)
  // Function to refresh position immediately
  const refreshPosition = useCallback(async () => {
    const walletAddr = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!walletAddr || !tokenAddress) {
      return;
    }
    try {
      // If token balance is zero, clear position immediately
      if (tokenBalance <= 0.0001) {
        setOpenPosition(null);
        return;
      }
      
      const positions = await getOpenPositions(walletAddr);
      const position = positions.find((p) => p.mint === tokenAddress);
      
      // Double-check: if we still have zero tokens, don't show position
      if (tokenBalance <= 0.0001) {
        setOpenPosition(null);
        return;
      }
      
      setOpenPosition(position || null);
      return position; // Return position for retry logic
    } catch (err) {
      console.error("Error refreshing position:", err);
      return null;
    }
  }, [walletAddress, connected, publicKey, tokenAddress, tokenBalance]);

  // Smart position refresh with retries (for after buy - waits for position to be created)
  // Optimized: Reduced default retries and delay for faster response
  const refreshPositionWithRetry = useCallback(async (maxRetries = 5, initialDelay = 100) => {
    const walletAddr = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!walletAddr || !tokenAddress) {
      return null;
    }

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const delay = initialDelay * Math.pow(1.5, attempt); // Exponential backoff
      
      if (attempt > 0) {
        // Wait before retrying (except first attempt)
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      try {
        const positions = await getOpenPositions(walletAddr);
        const position = positions.find((p) => p.mint === tokenAddress);
        
        if (position) {
          setOpenPosition(position);
          return position;
        }
        
        if (attempt < maxRetries - 1) {
        }
      } catch (err) {
        // Silently handle position fetch errors
        if (attempt === maxRetries - 1) {
          // Last attempt failed
          throw err;
        }
      }
    }

    // Position not found after all retries
    // Position not found after retries
    return null;
  }, [walletAddress, connected, publicKey, tokenAddress]);

  const fetchTokenBalanceManually = useCallback(async () => {
    const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!balanceWalletAddress || !tokenAddress) {
      return;
    }

    const fetchStartTime = performance.now();
    try {
      logger.debug('API_CALL', `Token balance fetch started (manual)`, { 
        wallet: balanceWalletAddress.substring(0, 8), 
        mint: tokenAddress.substring(0, 8),
        startTime: fetchStartTime
      });
      const balanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
      const fetchEndTime = performance.now();
      const fetchDuration = fetchEndTime - fetchStartTime;
      
      if (balanceData.error) {
        logger.error('API_CALL', `Token balance fetch error`, { 
          wallet: balanceWalletAddress.substring(0, 8), 
          mint: tokenAddress.substring(0, 8),
          error: balanceData.error,
          duration: fetchDuration
        });
        return;
      }
      
      const newBalance = balanceData.balanceUi || 0;
      const oldBalance = tokenBalance;
      setTokenBalance(newBalance);
      setTokenDecimals(balanceData.decimals || 6);
      
      logger.info('UI_STATE', `Token balance updated (manual)`, { 
        wallet: balanceWalletAddress.substring(0, 8), 
        mint: tokenAddress.substring(0, 8),
        oldBalance, 
        newBalance,
        decimals: balanceData.decimals || 6,
        duration: fetchDuration,
        startTime: fetchStartTime,
        endTime: fetchEndTime
      });
        
      // If balance is zero, clear position
      if (newBalance <= 0.0001) {
        setOpenPosition(null);
        hasBoughtOnPageRef.current = false;
      } else {
        // If we have tokens, mark that we've bought (so polling continues)
        hasBoughtOnPageRef.current = true;
      }
    } catch (err) {
      const fetchEndTime = performance.now();
      logger.error('API_CALL', `Token balance fetch exception`, { 
        wallet: balanceWalletAddress.substring(0, 8), 
        mint: tokenAddress.substring(0, 8),
        error: err.message,
        duration: fetchEndTime - fetchStartTime
      });
    }
  }, [walletAddress, connected, publicKey, tokenAddress, tokenBalance]);

  // Fetch token balance when toggling to sell mode
  useEffect(() => {
    if (mode === "sell" && connected && publicKey && tokenAddress) {
      fetchTokenBalanceManually();
    }
  }, [mode, connected, publicKey, tokenAddress]);

  // Poll token balance - always fetch at least once, then poll if we have tokens
  useEffect(() => {
    const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!balanceWalletAddress || !tokenAddress) {
      setTokenBalance(0);
      return;
    }

    let hasFetchedOnce = false; // Track if we've fetched at least once

    const fetchTokenBalance = async () => {
      try {
        logger.debug('BALANCE', `Fetching token balance for wallet: ${balanceWalletAddress.substring(0, 8)}..., mint: ${tokenAddress.substring(0, 8)}...`);
        const balanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
        
        hasFetchedOnce = true;
        
        if (balanceData.error) {
          logger.error('BALANCE', `Token balance lookup failed`, { error: balanceData.error, wallet: balanceWalletAddress.substring(0, 8), mint: tokenAddress.substring(0, 8) });
          // Don't update balance on error, but don't stop polling either
          return;
        }
        
          const newBalance = balanceData.balanceUi || 0;
        logger.debug('BALANCE', `Token balance: ${newBalance} (decimals: ${balanceData.decimals || 6})`);
          setTokenBalance(newBalance);
          setTokenDecimals(balanceData.decimals || 6);
          
          // If balance becomes 0, stop polling and clear position
          if (newBalance <= 0.0001) {
            hasBoughtOnPageRef.current = false;
            setOpenPosition(null);
        } else {
          // If we have tokens, mark that we've bought (so polling continues)
          hasBoughtOnPageRef.current = true;
        }
      } catch (err) {
        logger.error('BALANCE', 'Exception fetching token balance', { error: err.message, stack: err.stack });
        hasFetchedOnce = true;
      }
    };

    // Always fetch at least once when wallet/token changes
    fetchTokenBalance();
    
    // Then poll every 3 seconds if we have tokens or haven't fetched yet
    // Stop polling if balance is 0 and we've fetched at least once
    const intervalId = setInterval(() => {
      // Continue polling if:
      // 1. We have tokens (tokenBalance > 0), OR
      // 2. We haven't fetched yet (initial load), OR  
      // 3. User bought on this page
      if (tokenBalance > 0 || !hasFetchedOnce || hasBoughtOnPageRef.current) {
        fetchTokenBalance();
      }
    }, 3000);
    
    tokenBalanceIntervalRef.current = intervalId;
    
    return () => {
      if (tokenBalanceIntervalRef.current) {
        clearInterval(tokenBalanceIntervalRef.current);
      }
    };
  }, [walletAddress, connected, publicKey, tokenAddress]);

  // Get open position and fetch token balance if position exists
  useEffect(() => {
    const walletAddr = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!walletAddr || !tokenAddress) return;

    const fetchPosition = async () => {
      try {
        const positions = await getOpenPositions(walletAddr);
        const position = positions.find((p) => p.mint === tokenAddress);
        setOpenPosition(position || null);
        
        // If position exists, fetch token balance to show it immediately
        if (position) {
          fetchTokenBalanceManually();
        }
      } catch (err) {
        console.error("Error fetching position:", err);
      }
    };

    fetchPosition();
    positionIntervalRef.current = setInterval(fetchPosition, 10000);
    return () => {
      if (positionIntervalRef.current) {
        clearInterval(positionIntervalRef.current);
      }
    };
  }, [walletAddress, connected, publicKey, tokenAddress, fetchTokenBalanceManually]);

  // Visibility-based refresh - refresh all data when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && connected && publicKey) {
        // Tab became visible - refresh all data immediately
        
        // Refresh SOL balance
        const fetchBalance = async () => {
          const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
          if (!balanceWalletAddress) return;
          try {
            const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
            if (response.ok) {
              const data = await response.json();
              setBalance(data.balance || 0);
            }
          } catch (err) {
            // Silently handle balance errors
          }
        };
        fetchBalance();
        
        // Refresh token balance if we have tokens or have bought
        if (tokenBalance > 0 || hasBoughtOnPageRef.current) {
          fetchTokenBalanceManually();
        }
        
        // Refresh position
        refreshPosition();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [connected, publicKey, tokenBalance, fetchTokenBalanceManually, refreshPosition]);

  // Function to reset polling timers after actions (buy/sell)
  const resetPollingTimers = useCallback(() => {
    // Reset SOL balance polling
    if (solBalanceIntervalRef.current) {
      clearInterval(solBalanceIntervalRef.current);
    }
    const resetBalanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (resetBalanceWalletAddress && connection) {
      const fetchBalance = async () => {
        try {
          const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
          const response = await fetch(`${apiUrl}/api/wallet/balance?address=${resetBalanceWalletAddress}`);
          if (response.ok) {
            const data = await response.json();
            setBalance(data.balance || 0);
          }
        } catch (err) {
          // Silently handle balance errors
        }
      };
      fetchBalance();
      solBalanceIntervalRef.current = setInterval(fetchBalance, 5000); // Every 5 seconds (faster refresh)
    }
    
    // Reset token balance polling
    if (tokenBalanceIntervalRef.current) {
      clearInterval(tokenBalanceIntervalRef.current);
    }
    const pollBalanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (pollBalanceWalletAddress && tokenAddress && (tokenBalance > 0 || hasBoughtOnPageRef.current)) {
      const fetchTokenBalance = async () => {
        try {
          const balanceData = await getTokenBalance(pollBalanceWalletAddress, tokenAddress);
          if (!balanceData.error) {
            const newBalance = balanceData.balanceUi || 0;
            setTokenBalance(newBalance);
            setTokenDecimals(balanceData.decimals || 6);
            if (newBalance <= 0.0001) {
              hasBoughtOnPageRef.current = false;
              setOpenPosition(null);
            }
          }
        } catch (err) {
          console.error("Error fetching token balance:", err);
        }
      };
      fetchTokenBalance();
      tokenBalanceIntervalRef.current = setInterval(fetchTokenBalance, 3000);
    }
    
    // Reset position polling
    if (positionIntervalRef.current) {
      clearInterval(positionIntervalRef.current);
    }
    const walletAddr = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (walletAddr && tokenAddress) {
      const fetchPosition = async () => {
        try {
          const positions = await getOpenPositions(walletAddr);
          const position = positions.find((p) => p.mint === tokenAddress);
          setOpenPosition(position || null);
          if (position) {
            fetchTokenBalanceManually();
          }
        } catch (err) {
          console.error("Error fetching position:", err);
        }
      };
      fetchPosition();
      positionIntervalRef.current = setInterval(fetchPosition, 10000);
    }
  }, [walletAddress, connected, publicKey, tokenAddress, tokenBalance, connection, fetchTokenBalanceManually]);

  // Calculate real-time PnL and total position value using current price
  const livePnL = useMemo(() => {
    // If no tokens, show zero (position is effectively closed)
    if (tokenBalance <= 0.0001) {
      return { pnlPct: 0, pnlSol: 0, totalPosition: 0 };
    }

    if (!openPosition || !currentPrice) {
      return { pnlPct: 0, pnlSol: 0, totalPosition: 0 };
    }

    const entryPrice = parseFloat(openPosition.entry_price || 0);
    const sizeSol = parseFloat(openPosition.size_sol || 0);

    if (entryPrice <= 0 || sizeSol <= 0) {
      return { pnlPct: 0, pnlSol: 0, totalPosition: 0 };
    }

    // Calculate PnL percentage: (currentPrice / entryPrice - 1) * 100
    const pnlPct = ((currentPrice / entryPrice) - 1) * 100;
    
    // Calculate PnL in SOL: sizeSol * (pnlPct / 100)
    const pnlSol = sizeSol * (pnlPct / 100);

    // Calculate total position value based on actual token balance, not position size
    // If user has fewer tokens than the original position, scale down the position value
    const originalTokenAmount = parseFloat(openPosition.token_amount || 0);
    if (originalTokenAmount > 0) {
      // Scale the position value by the ratio of current tokens to original tokens
      const tokenRatio = tokenBalance / originalTokenAmount;
      const totalPosition = sizeSol * (currentPrice / entryPrice) * tokenRatio;
      return { pnlPct, pnlSol, totalPosition };
    }

    // Fallback: use full position size
    const totalPosition = sizeSol * (currentPrice / entryPrice);
    return { pnlPct, pnlSol, totalPosition };
  }, [openPosition, currentPrice, tokenBalance]);

  // Format number with abbreviations (1k, 10k, 100k, 1M, 10M, 100M, 1B)
  const formatAmount = (num) => {
    if (num === 0) return "0";
    if (num < 1000) return num.toFixed(2);
    if (num < 10000) return (num / 1000).toFixed(2) + "k";
    if (num < 100000) return (num / 1000).toFixed(1) + "k";
    if (num < 1000000) return (num / 1000).toFixed(0) + "k";
    if (num < 10000000) return (num / 1000000).toFixed(2) + "M";
    if (num < 100000000) return (num / 1000000).toFixed(1) + "M";
    if (num < 1000000000) return (num / 1000000).toFixed(0) + "M";
    if (num < 10000000000) return (num / 1000000000).toFixed(2) + "B";
    if (num < 100000000000) return (num / 1000000000).toFixed(1) + "B";
    return (num / 1000000000).toFixed(0) + "B";
  };

  // Helper function to wait for balances to update (ensures data is ready before success)
  // MUST be defined before any early returns to comply with React Hooks rules
  const waitForBalancesToUpdate = useCallback(async (expectedSolChange, expectedTokenChange, maxRetries = 5, delay = 300) => {
    const initialSolBalance = balance;
    const initialTokenBalance = tokenBalance;
    
    // Always update balances immediately on first attempt (don't wait)
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      if (attempt > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
      
      try {
        // Refresh SOL balance - CRITICAL: Update immediately
        const waitBalanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
        if (!waitBalanceWalletAddress) return false;
        const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
        const solRes = await fetch(`${apiUrl}/api/wallet/balance?address=${waitBalanceWalletAddress}`);
        let newSolBalance = initialSolBalance;
        if (solRes.ok) {
          const solData = await solRes.json();
          newSolBalance = solData.balance || 0;
          // CRITICAL: Update SOL balance immediately on every attempt
          setBalance(newSolBalance);
        }
        
        // Refresh token balance
        const tokenData = await getTokenBalance(waitBalanceWalletAddress, tokenAddress);
        const newTokenBalance = tokenData.balanceUi || 0;
        
        // Always update balances (don't wait for change detection)
        setTokenBalance(newTokenBalance);
        setTokenDecimals(tokenData.decimals || 6);
        
        // Check if balances have changed (within tolerance)
        const solChanged = Math.abs(newSolBalance - initialSolBalance) > 0.0001;
        const tokenChanged = Math.abs(newTokenBalance - initialTokenBalance) > 0.0001;
        
        // If we expected changes and they happened, we're good
        if ((expectedSolChange === null || solChanged) && (expectedTokenChange === null || tokenChanged)) {
          return true;
        }
        
        // For sells, if token balance is 0 or very low, consider it updated
        if (expectedTokenChange && newTokenBalance <= 0.0001 && initialTokenBalance > 0.0001) {
          return true;
        }
      } catch (err) {
        // Silently fail balance checks
      }
    }
    
    // Final update attempt - always update with latest values
    try {
      const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
      if (!balanceWalletAddress) return false;
      
      const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
      const solRes = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
      if (solRes.ok) {
        const solData = await solRes.json();
        const solBalance = solData.balance || 0;
        setBalance(solBalance); // CRITICAL: Always update SOL balance
      }
      const tokenData = await getTokenBalance(balanceWalletAddress, tokenAddress);
      setTokenBalance(tokenData.balanceUi || 0);
      setTokenDecimals(tokenData.decimals || 6);
      } catch (err) {
        // Silently handle balance update errors
      }
    
    return false; // Couldn't verify, but proceed anyway
  }, [balance, tokenBalance, walletAddress, connected, publicKey, tokenAddress]);

  // Prefetch buy data when user shows intent (hover, typing amount)
  const triggerPrefetch = useCallback(() => {
    // Only prefetch if:
    // - Wallet is connected
    // - We're in buy mode
    // - We have a token address
    // - We're not already prefetching
    const prefetchWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!prefetchWalletAddress || mode !== "buy" || !tokenAddress || isPrefetchingRef.current) {
      return;
    }
    
    // Check if we have fresh cached data (less than 10 seconds old)
    if (prefetchCacheRef.current) {
      const cacheAge = Date.now() - (prefetchCacheRef.current.timestamp * 1000);
      if (cacheAge < 10000) {
        // Cache is fresh, no need to prefetch
        return;
      }
    }
    
    // Clear any existing prefetch timeout
    if (prefetchTimeoutRef.current) {
      clearTimeout(prefetchTimeoutRef.current);
    }
    
    // Debounce prefetch by 300ms (wait for user to finish typing)
    prefetchTimeoutRef.current = setTimeout(async () => {
      if (isPrefetchingRef.current) return;
      
      isPrefetchingRef.current = true;
      try {
        const prefetchData = await prefetchBuyData(tokenAddress, prefetchWalletAddress);
        if (!prefetchData.error) {
          prefetchCacheRef.current = prefetchData;
          console.log("✅ Prefetched buy data cached");
        }
      } catch (err) {
        // Silently fail - prefetch errors shouldn't block the UI
      } finally {
        isPrefetchingRef.current = false;
      }
    }, 300);
  }, [walletAddress, connected, publicKey, mode, tokenAddress]);
  
  // Prefetch when mode changes to "buy"
  useEffect(() => {
    if (mode === "buy" && connected && publicKey && tokenAddress) {
      triggerPrefetch();
    }
  }, [mode, connected, publicKey, tokenAddress, triggerPrefetch]);
  
  // Prefetch when user types in amount (debounced)
  useEffect(() => {
    if (mode === "buy" && amount && parseFloat(amount) > 0) {
      triggerPrefetch();
    }
  }, [amount, mode, triggerPrefetch]);

  // Only show for Solana tokens
  if (chainId !== "solana") {
    return null;
  }

  const handlePercentageClick = (percent) => {
    if (mode === "buy") {
      // Buy: percentage of SOL balance
      setAmount((balance * percent).toFixed(4));
      // Trigger prefetch when percentage is clicked (user shows intent)
      triggerPrefetch();
    } else {
      // Sell: percentage of token balance
      if (percent === 1.0) {
        setAmount(tokenBalance.toString());
      } else {
        setAmount((tokenBalance * percent).toFixed(4));
      }
    }
  };

  const clearButtonStatus = () => {
    if (executionTimeoutRef.current) {
      clearTimeout(executionTimeoutRef.current);
    }
    executionTimeoutRef.current = setTimeout(() => {
      setButtonStatus(null);
      setErrorMessage(null);
      updateExecutionStage(null);
    }, 3000);
  };

  const handleBuy = async () => {
    const buyStartTime = performance.now();
    const buyEventId = `buy_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if we have a wallet (either from vault or Phantom)
    const buyWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!buyWalletAddress) {
      logger.warn('UI_EVENT', 'Buy button clicked - No wallet available', { eventId: buyEventId });
      setErrorMessage("Connect your wallet or unlock your vault");
      setButtonStatus("failed");
      clearButtonStatus();
      return;
    }
    
    const amountInSol = parseFloat(amount);
    logger.info('UI_EVENT', `Buy button clicked`, { 
      eventId: buyEventId, 
      token: tokenAddress.substring(0, 8), 
      wallet: buyWalletAddress.substring(0, 8), 
      amount: amountInSol,
      currentBalance: balance,
      timestamp: buyStartTime
    });

    if (!amount || amountInSol <= 0) {
      logger.warn('UI_EVENT', 'Buy button clicked - Invalid amount', { eventId: buyEventId, amount });
      setErrorMessage("Enter an amount");
      setButtonStatus("failed");
      clearButtonStatus();
      return;
    }

    setIsExecuting(true);
    setButtonStatus(null);
    setErrorMessage(null);
    updateExecutionStage("placing");
    
    logger.info('UI_STATE', 'Buy execution started', { eventId: buyEventId, stage: 'placing' });

    // Set 40-second timeout for the entire buy process (includes verification)
    executionTimeoutRef.current = setTimeout(() => {
      if (isExecuting && (executionStageRef.current === "placing" || executionStageRef.current === "verifying" || executionStageRef.current === "creating-position")) {
        updateExecutionStage("failed");
        setErrorMessage("Transaction timed out (40s) - retry");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
      }
    }, 40000);

    try {
      if (!connection || !buyWalletAddress) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        setErrorMessage("Connection error or no wallet available");
        setButtonStatus("failed");
        updateExecutionStage("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }

      // Quick balance check (use cached balance, don't wait for refresh)
      const amountInSol = parseFloat(amount);
      const MIN_RESERVE_SOL = 0.01;
      const availableBalance = Math.max(0, balance - MIN_RESERVE_SOL);
      
      if (balance <= 0) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        setErrorMessage("Not enough SOL");
        setButtonStatus("failed");
        updateExecutionStage("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }
      
      if (amountInSol > availableBalance) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        setErrorMessage("Not enough SOL");
        setButtonStatus("failed");
        updateExecutionStage("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }
      const currentSettings = mode === "buy" ? buySettings : sellSettings;
      
      // Use cached prefetch data if available and fresh (less than 10 seconds old)
      let cachedData = null;
      if (prefetchCacheRef.current) {
        const cacheAge = Date.now() - (prefetchCacheRef.current.timestamp * 1000);
        if (cacheAge < 10000) {
          cachedData = prefetchCacheRef.current;
          console.log(`⚡ Using prefetched data (${Math.round(cacheAge/1000)}s old) - skipping graduation check`);
        } else {
          // Cache expired, clear it
          prefetchCacheRef.current = null;
        }
      }
      
      const apiStartTime = performance.now();
      logger.info('API_CALL', 'executeBuy API call started', { 
        eventId: buyEventId, 
        token: tokenAddress.substring(0, 8), 
        amount: amountInSol,
        startTime: apiStartTime
      });
      
      const result = await executeBuy(
        tokenAddress,
        amountInSol,
        buyWalletAddress,
        currentSettings.slippage,
        currentSettings.priorityFee,
        currentSettings.tip,
        cachedData
      );
      
      const apiEndTime = performance.now();
      const apiDuration = apiEndTime - apiStartTime;
      logger.info('API_CALL', 'executeBuy API call completed', { 
        eventId: buyEventId, 
        duration: apiDuration,
        startTime: apiStartTime,
        endTime: apiEndTime,
        hasError: !!result.error,
        hasTransaction: !!(result.transaction || result.instructions),
        hasSignature: !!result.signature
      });
      
      // Clear prefetch cache after use (force fresh data next time)
      prefetchCacheRef.current = null;

      // Check for errors
      if (result.error) {
        logger.error('TRADING', 'Buy transaction build failed', { eventId: buyEventId, error: result.error });
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage(result.error || "Failed");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }

      // Handle backend-signed transaction (signature returned immediately, confirmation pending)
      if (result.autoSigned && result.signature) {
        const signature = result.signature;
        const expectedOutput = result.expectedOutputAmount || "0";
        // Jupiter returns outAmount in the token's smallest unit, so we need to divide by 10^decimals
        // Use tokenDecimals from state (defaults to 6, but will be updated when balance is fetched)
        const tokenAmount = parseFloat(expectedOutput) / Math.pow(10, tokenDecimals);
        const entryPrice = currentPrice || 0;
        
        // Update execution stage to "verifying"
        updateExecutionStage("verifying");
        
        try {
          // Verify transaction is confirmed on-chain (didn't fail due to slippage, etc.)
          await verifyTransactionOnChain(signature, connection, 30000);
          
          // ✅ SUCCESS: Transaction confirmed - show success immediately (don't wait for balances)
          const balanceWalletAddress = buyWalletAddress || walletAddress || (publicKey ? publicKey.toString() : null);
          const preBuySolBalance = balance; // Track SOL balance before buy
          const preBuyTokenBalance = tokenBalance || 0; // Track token balance before buy
          
          if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
          updateExecutionStage("success");
          setButtonStatus("success");
          setAmount("");
          setIsExecuting(false);
          hasBoughtOnPageRef.current = true;
          clearButtonStatus();
          
          // Reset polling timers
          resetPollingTimers();
          
          // 🔄 BACKGROUND: Poll balances until they change (detect buy went through)
          (async () => {
            const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
            let balancesUpdated = false;
            let pollAttempts = 0;
            const maxPollAttempts = 30; // 30 seconds max (30 * 1s)
            
            while (!balancesUpdated && pollAttempts < maxPollAttempts) {
              try {
                pollAttempts++;
                
                // Fetch SOL balance
                const solBalanceRes = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
                let newSolBalance = preBuySolBalance;
                if (solBalanceRes.ok) {
                  const solData = await solBalanceRes.json();
                  newSolBalance = solData.balance || 0;
                }
                
                // Fetch token balance
                const tokenBalanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
                let newTokenBalance = preBuyTokenBalance;
                if (!tokenBalanceData.error) {
                  if (tokenBalanceData.decimals) {
                    setTokenDecimals(tokenBalanceData.decimals);
                  }
                  if (tokenBalanceData.balanceUi !== undefined) {
                    newTokenBalance = tokenBalanceData.balanceUi;
                  }
                }
                
                // Check if balances changed (SOL decreased, token balance increased)
                const solChanged = newSolBalance < preBuySolBalance;
                const tokenChanged = newTokenBalance > preBuyTokenBalance;
                
                if (solChanged || tokenChanged) {
                  // Balances updated - transaction reflected on-chain
                  setBalance(newSolBalance);
                  setTokenBalance(newTokenBalance);
                  balancesUpdated = true;
                  logger.info('TRADING', 'Balances updated after buy', { 
                    eventId: buyEventId,
                    preBuySol: preBuySolBalance,
                    newSol: newSolBalance,
                    preBuyToken: preBuyTokenBalance,
                    newToken: newTokenBalance,
                    attempts: pollAttempts
                  });
                } else {
                  // Balances haven't updated yet - wait 1 second and try again
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              } catch (err) {
                // Log error but continue polling
                logger.debug('TRADING', 'Balance poll error (continuing)', { 
                  eventId: buyEventId, 
                  error: err.message,
                  attempt: pollAttempts
                });
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
            
            if (!balancesUpdated) {
              logger.warn('TRADING', 'Balance polling timeout - balances may be stale', { 
                eventId: buyEventId,
                attempts: pollAttempts
              });
              // Still try to fetch balances one more time
              try {
                const solBalanceRes = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
                if (solBalanceRes.ok) {
                  const solData = await solBalanceRes.json();
                  setBalance(solData.balance || 0);
                }
                const tokenBalanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
                if (!tokenBalanceData.error && tokenBalanceData.balanceUi !== undefined) {
                  setTokenBalance(tokenBalanceData.balanceUi);
                }
              } catch (err) {
                // Ignore final fetch errors
              }
            }
          })();
          
          // 🔄 BACKGROUND: Create position in database (non-blocking)
          (async () => {
            try {
              const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
              const confirmRes = await fetch(`${apiUrl}/api/trade/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  txSignature: signature,
                  mint: tokenAddress,
                  walletAddress: balanceWalletAddress,
                  side: 'BUY',
                  price: entryPrice,
                  sizeSol: amountInSol,
                  tokenAmount: tokenAmount
                })
              });
              
              if (confirmRes.ok) {
                const confirmData = await confirmRes.json();
                if (confirmData.position) {
                  setOpenPosition(confirmData.position);
                }
              }
            } catch (err) {
              // Log but don't affect UI - success already shown
              logger.error('TRADING', 'Position creation error (non-blocking)', { 
                eventId: buyEventId, 
                error: err.message 
              });
            }
          })();
          
        } catch (error) {
          // Handle verification failures
          logger.error('TRADING', 'Buy verification failed', { 
            eventId: buyEventId, 
            error: error.message,
            signature: signature.substring(0, 8) + '...',
            stage: executionStageRef.current
          });
          
          if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
          
          // Determine error message
          let errorMessage = "Transaction verification failed";
          if (error.message.includes('Transaction failed')) {
            errorMessage = "Transaction failed on-chain. Please try again.";
          } else if (error.message.includes('timeout')) {
            errorMessage = "Transaction verification timed out. Please check your wallet.";
          } else {
            errorMessage = error.message || "Transaction verification failed";
          }
          
          updateExecutionStage("failed");
          setErrorMessage(errorMessage);
          setButtonStatus("failed");
          setIsExecuting(false);
          clearButtonStatus();
        }
        
        return;
      }

      // Handle frontend signing
      if (result.transaction || result.instructions) {
        // Keep execution stage as "placing" - waiting for signature
        
        let transaction;
        let signature;
        
        if (result.isPumpFun && result.instructions) {
          transaction = new Transaction();
          transaction.recentBlockhash = result.recentBlockhash;
          transaction.feePayer = new PublicKey(result.feePayer);
          
          for (const ixData of result.instructions) {
            const instruction = new TransactionInstruction({
              programId: new PublicKey(ixData.programId),
              keys: ixData.keys.map(key => ({
                pubkey: new PublicKey(key.pubkey),
                isSigner: key.isSigner,
                isWritable: key.isWritable
              })),
              data: Buffer.from(ixData.data)
            });
            transaction.add(instruction);
          }
          
          // Sign transaction - use vault keypair if available, otherwise Phantom wallet
          let signedTx;
          if (isUnlocked && vault && vaultKey) {
            // Use vault keypair for signing
            try {
              const keypair = await getPrimaryWalletKeypair(vault, vaultKey);
              transaction.partialSign(keypair);
              signedTx = transaction;
            } catch (error) {
              console.error("Error signing with vault keypair:", error);
              // Fallback to Phantom wallet if vault signing fails
          if (!signTransaction) {
                throw new Error("Vault signing failed and wallet does not support transaction signing");
              }
              signedTx = await signTransaction(transaction);
            }
          } else {
            // Use Phantom wallet for signing
            if (!signTransaction) {
              throw new Error("Wallet does not support transaction signing. Please connect a wallet or unlock your vault.");
          }
            signedTx = await signTransaction(transaction);
          }
          
          // Keep execution stage as "placing" - submitting transaction
          signature = await connection.sendRawTransaction(signedTx.serialize(), {
            skipPreflight: false,
            preflightCommitment: 'processed',
            maxRetries: 3
          });
        } else if (result.transaction) {
          const transactionBuffer = Buffer.from(result.transaction, 'base64');
          
          // Try to detect transaction type by attempting to deserialize
          // Versioned transactions must be deserialized with VersionedTransaction.deserialize()
          // Legacy transactions use Transaction.from()
          let transaction;
          let isVersioned = false;
          
          try {
            // Try versioned first (most common for Jupiter)
            transaction = VersionedTransaction.deserialize(transactionBuffer);
            isVersioned = true;
          } catch (e) {
            // If that fails, try legacy
            try {
          transaction = Transaction.from(transactionBuffer);
              isVersioned = false;
            } catch (e2) {
              throw new Error(`Failed to deserialize transaction: ${e2.message}`);
            }
          }
          
          // Sign transaction - use vault keypair if available, otherwise Phantom wallet
          if (isVersioned) {
            // Handle versioned transaction (V0)
            if (isUnlocked && vault && vaultKey) {
              // Use vault keypair for signing
              try {
                const keypair = await getPrimaryWalletKeypair(vault, vaultKey);
                transaction.sign([keypair]);
                // Send signed transaction directly
                signature = await connection.sendTransaction(transaction, {
                  skipPreflight: false,
                  preflightCommitment: 'processed',
                  maxRetries: 3
                });
              } catch (error) {
                console.error("Error signing versioned transaction with vault keypair:", error);
                // Fallback to Phantom wallet if vault signing fails
                if (!sendTransaction) {
                  throw new Error("Vault signing failed and wallet does not support transaction signing");
                }
          signature = await sendTransaction(transaction, connection, {
            skipPreflight: false,
            maxRetries: 3
          });
              }
            } else {
              // Use Phantom wallet for signing versioned transaction
              if (!sendTransaction) {
                throw new Error("Wallet does not support transaction signing. Please connect a wallet or unlock your vault.");
              }
              signature = await sendTransaction(transaction, connection, {
                skipPreflight: false,
                maxRetries: 3
              });
            }
          } else {
            // Handle legacy transaction
            if (isUnlocked && vault && vaultKey) {
              // Use vault keypair for signing
              try {
                const keypair = await getPrimaryWalletKeypair(vault, vaultKey);
                transaction.partialSign(keypair);
                // Send signed transaction directly
                signature = await connection.sendRawTransaction(transaction.serialize(), {
                  skipPreflight: false,
                  preflightCommitment: 'processed',
                  maxRetries: 3
                });
              } catch (error) {
                console.error("Error signing legacy transaction with vault keypair:", error);
                // Fallback to Phantom wallet if vault signing fails
                if (!sendTransaction) {
                  throw new Error("Vault signing failed and wallet does not support transaction signing");
                }
                signature = await sendTransaction(transaction, connection, {
                  skipPreflight: false,
                  maxRetries: 3
                });
              }
            } else {
              // Use Phantom wallet for signing
              if (!sendTransaction) {
                throw new Error("Wallet does not support transaction signing. Please connect a wallet or unlock your vault.");
              }
              signature = await sendTransaction(transaction, connection, {
                skipPreflight: false,
                maxRetries: 3
              });
            }
          }
        } else {
          throw new Error("No transaction or instructions provided");
        }
        
        // Transaction sent - now verify before showing success
        const expectedOutput = result.expectedOutputAmount || "0";
        // Jupiter returns outAmount in the token's smallest unit, so we need to divide by 10^decimals
        // Use tokenDecimals from state (defaults to 6, but will be updated when balance is fetched)
        const tokenAmount = parseFloat(expectedOutput) / Math.pow(10, tokenDecimals);
        const entryPrice = currentPrice || 0;
        
        // Update execution stage to "verifying"
        updateExecutionStage("verifying");
        
        try {
          // Verify transaction is confirmed on-chain (didn't fail due to slippage, etc.)
          await verifyTransactionOnChain(signature, connection, 30000);
          
          // ✅ SUCCESS: Transaction confirmed - show success immediately (don't wait for balances)
          const balanceWalletAddress = buyWalletAddress || walletAddress || (publicKey ? publicKey.toString() : null);
          const preBuySolBalance = balance; // Track SOL balance before buy
          const preBuyTokenBalance = tokenBalance || 0; // Track token balance before buy
          
          if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
          updateExecutionStage("success");
          setButtonStatus("success");
          setAmount("");
          setIsExecuting(false);
          hasBoughtOnPageRef.current = true;
          clearButtonStatus();
          
          // Reset polling timers
          resetPollingTimers();
          
          // 🔄 BACKGROUND: Poll balances until they change (detect buy went through)
          (async () => {
            const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
            let balancesUpdated = false;
            let pollAttempts = 0;
            const maxPollAttempts = 30; // 30 seconds max (30 * 1s)
            
            while (!balancesUpdated && pollAttempts < maxPollAttempts) {
              try {
                pollAttempts++;
                
                // Fetch SOL balance
                const solBalanceRes = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
                let newSolBalance = preBuySolBalance;
                if (solBalanceRes.ok) {
                  const solData = await solBalanceRes.json();
                  newSolBalance = solData.balance || 0;
                }
                
                // Fetch token balance
                const tokenBalanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
                let newTokenBalance = preBuyTokenBalance;
                if (!tokenBalanceData.error) {
                  if (tokenBalanceData.decimals) {
                    setTokenDecimals(tokenBalanceData.decimals);
                  }
                  if (tokenBalanceData.balanceUi !== undefined) {
                    newTokenBalance = tokenBalanceData.balanceUi;
                  }
                }
                
                // Check if balances changed (SOL decreased, token balance increased)
                const solChanged = newSolBalance < preBuySolBalance;
                const tokenChanged = newTokenBalance > preBuyTokenBalance;
                
                if (solChanged || tokenChanged) {
                  // Balances updated - transaction reflected on-chain
                  setBalance(newSolBalance);
                  setTokenBalance(newTokenBalance);
                  balancesUpdated = true;
                  logger.info('TRADING', 'Balances updated after buy', { 
                    eventId: buyEventId,
                    preBuySol: preBuySolBalance,
                    newSol: newSolBalance,
                    preBuyToken: preBuyTokenBalance,
                    newToken: newTokenBalance,
                    attempts: pollAttempts
                  });
                } else {
                  // Balances haven't updated yet - wait 1 second and try again
                  await new Promise(resolve => setTimeout(resolve, 1000));
                }
              } catch (err) {
                // Log error but continue polling
                logger.debug('TRADING', 'Balance poll error (continuing)', { 
                  eventId: buyEventId, 
                  error: err.message,
                  attempt: pollAttempts
                });
                await new Promise(resolve => setTimeout(resolve, 1000));
              }
            }
            
            if (!balancesUpdated) {
              logger.warn('TRADING', 'Balance polling timeout - balances may be stale', { 
                eventId: buyEventId,
                attempts: pollAttempts
              });
              // Still try to fetch balances one more time
              try {
                const solBalanceRes = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddress}`);
                if (solBalanceRes.ok) {
                  const solData = await solBalanceRes.json();
                  setBalance(solData.balance || 0);
                }
                const tokenBalanceData = await getTokenBalance(balanceWalletAddress, tokenAddress);
                if (!tokenBalanceData.error && tokenBalanceData.balanceUi !== undefined) {
                  setTokenBalance(tokenBalanceData.balanceUi);
                }
              } catch (err) {
                // Ignore final fetch errors
              }
            }
          })();
          
          // 🔄 BACKGROUND: Create position in database (non-blocking)
          (async () => {
            try {
              const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
              const confirmRes = await fetch(`${apiUrl}/api/trade/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  txSignature: signature,
                  mint: tokenAddress,
                  walletAddress: balanceWalletAddress,
                  side: 'BUY',
                  price: entryPrice,
                  sizeSol: amountInSol,
                  tokenAmount: tokenAmount
                })
              });
              
              if (confirmRes.ok) {
                const confirmData = await confirmRes.json();
                if (confirmData.position) {
                  setOpenPosition(confirmData.position);
                }
              }
            } catch (err) {
              // Log but don't affect UI - success already shown
              logger.error('TRADING', 'Position creation error (non-blocking)', { 
                eventId: buyEventId, 
                error: err.message 
              });
            }
          })();
          
        } catch (error) {
          // Handle verification failures
          logger.error('TRADING', 'Buy verification failed', { 
            eventId: buyEventId, 
            error: error.message,
            signature: signature.substring(0, 8) + '...',
            stage: executionStageRef.current
          });
          
          if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
          
          // Determine error message
          let errorMessage = "Transaction verification failed";
          if (error.message.includes('Transaction failed')) {
            errorMessage = "Transaction failed on-chain. Please try again.";
          } else if (error.message.includes('timeout')) {
            errorMessage = "Transaction verification timed out. Please check your wallet.";
          } else {
            errorMessage = error.message || "Transaction verification failed";
          }
          
          updateExecutionStage("failed");
          setErrorMessage(errorMessage);
          setButtonStatus("failed");
          setIsExecuting(false);
          clearButtonStatus();
        }
      } else {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage(result.error || "Failed");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
      }
    } catch (err) {
      if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
      updateExecutionStage("failed");
      setErrorMessage(err.message || "Failed");
      setButtonStatus("failed");
      setIsExecuting(false);
      clearButtonStatus();
    }
  };

  const handleSell = async () => {
    const sellStartTime = performance.now();
    const sellEventId = `sell_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Check if we have a wallet (either from vault or Phantom)
    const sellWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
    if (!sellWalletAddress) {
      logger.warn('UI_EVENT', 'Sell button clicked - No wallet available', { eventId: sellEventId });
      setErrorMessage("Connect your wallet or unlock your vault");
      setButtonStatus("failed");
      clearButtonStatus();
      return;
    }
    
    const tokenAmount = parseFloat(amount);
    logger.info('UI_EVENT', `Sell button clicked`, { 
      eventId: sellEventId, 
      token: tokenAddress.substring(0, 8), 
      wallet: sellWalletAddress.substring(0, 8), 
      amount: tokenAmount,
      currentTokenBalance: tokenBalance,
      currentSolBalance: balance,
      timestamp: sellStartTime
    });

    if (!tokenAddress) {
      setErrorMessage("Token address required");
      setButtonStatus("failed");
      clearButtonStatus();
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      setErrorMessage("Enter an amount");
      setButtonStatus("failed");
      clearButtonStatus();
      return;
    }

    setIsExecuting(true);
    setButtonStatus(null);
    setErrorMessage(null);
    updateExecutionStage("placing");

    // Set 20-second timeout for the entire sell process
    executionTimeoutRef.current = setTimeout(() => {
      if (isExecuting && executionStageRef.current === "placing") {
        updateExecutionStage("failed");
        setErrorMessage("Transaction timed out (20s) - retry");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
      }
    }, 20000);

    // Capture initial SOL balance before sell (to detect change)
    const initialSolBalance = balance;

    try {
      // ⚡ INSTANT SELL: Use tokenBalance state directly (already updated instantly after buy)
      // Don't block on API call - use the state value that was updated immediately after buy success
      const currentTokenBalance = tokenBalance;
      
      if (currentTokenBalance <= 0.0001) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage("No tokens to sell");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }
      
      // Refresh balance in background (non-blocking) for verification, but don't wait for it
      if (sellWalletAddress && tokenAddress) {
        (async () => {
          try {
            logger.debug('TRADING', `Sell: Refreshing token balance in background for wallet: ${sellWalletAddress.substring(0, 8)}..., mint: ${tokenAddress.substring(0, 8)}...`);
            const balanceData = await getTokenBalance(sellWalletAddress, tokenAddress);
            if (!balanceData.error) {
              // Update state with real balance (may differ slightly due to timing, but close enough)
              setTokenBalance(balanceData.balanceUi || 0);
              setTokenDecimals(balanceData.decimals || 6);
            }
          } catch (err) {
            // Silently fail - we're using state value anyway
            logger.debug('TRADING', `Sell: Background balance refresh failed (non-blocking)`, { error: err.message });
          }
        })();
      }

      const tolerance = 0.0001;
      // tokenAmount already declared above at line 1323
      
      if (tokenAmount > currentTokenBalance + tolerance) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage("Not enough tokens");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }

      let finalTokenAmount = tokenAmount;
      if (Math.abs(tokenAmount - currentTokenBalance) < tolerance) {
        finalTokenAmount = currentTokenBalance;
      }

      // eslint-disable-next-line no-undef
      const tokenAmountRaw = BigInt(Math.round(finalTokenAmount * Math.pow(10, tokenDecimals)));

      const currentSettings = mode === "buy" ? buySettings : sellSettings;
      
      // Execute sell (timeout is handled by executionTimeoutRef)
      // sellWalletAddress was already defined at the start of handleSell
      if (!sellWalletAddress) {
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage("No wallet available. Please connect a wallet or unlock your vault.");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }
      
      const apiStartTime = performance.now();
      logger.info('API_CALL', 'executeSell API call started', { 
        eventId: sellEventId, 
        token: tokenAddress.substring(0, 8), 
        amount: tokenAmount,
        startTime: apiStartTime
      });
      
      const result = await executeSell(
        tokenAddress,
        tokenAmountRaw.toString(),
        sellWalletAddress,
        currentSettings.slippage,
        currentSettings.priorityFee,
        currentSettings.tip
      );
      
      const apiEndTime = performance.now();
      const apiDuration = apiEndTime - apiStartTime;
      logger.info('API_CALL', 'executeSell API call completed', { 
        eventId: sellEventId, 
        duration: apiDuration,
        startTime: apiStartTime,
        endTime: apiEndTime,
        hasError: !!result?.error,
        hasTransaction: !!(result?.transaction),
        hasSignature: !!result?.signature
      });

      // Check for errors first
      if (result?.error) {
        logger.error('TRADING', 'Sell transaction build failed', { eventId: sellEventId, error: result.error });
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("failed");
        setErrorMessage(result.error || "Failed");
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
        return;
      }

      // Handle backend-signed transaction (already confirmed on backend)
      if (result.autoSigned && result.signature) {
        // Keep execution stage as "placing" - transaction is in progress
        
        const expectedSolOutput = result.expectedOutputAmount ? parseFloat(result.expectedOutputAmount) / LAMPORTS_PER_SOL : 0;
        
        // Clear timeout and show success IMMEDIATELY
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("success");
        setButtonStatus("success");
        
        // ⚡ INSTANT UPDATE: Update SOL balance immediately when success message appears
        // Add the exact SOL amount received from the sell
        const newSolBalance = balance + expectedSolOutput;
        setBalance(newSolBalance);
        
        // Update token balance immediately (subtract tokens sold)
        const newTokenBalance = Math.max(0, currentTokenBalance - finalTokenAmount);
        setTokenBalance(newTokenBalance);
        
        // Update/close position immediately
        if (openPosition && openPosition.mint === tokenAddress) {
          const remainingTokens = Math.max(0, (parseFloat(openPosition.token_amount) || 0) - finalTokenAmount);
          const remainingSize = remainingTokens > 0.0001 ? 
            ((parseFloat(openPosition.size_sol) || 0) * (remainingTokens / (parseFloat(openPosition.token_amount) || 1))) : 0;
          
          if (remainingTokens <= 0.0001) {
            // Position fully closed
            setOpenPosition(null);
          } else {
            // Position partially closed - update amounts
            setOpenPosition({
              ...openPosition,
              token_amount: remainingTokens,
              size_sol: remainingSize
            });
          }
        } else {
          // No position to update
          setOpenPosition(null);
        }
        setAmount("");
        setIsExecuting(false);
        clearButtonStatus();
        
        // Reset polling timers (this will refresh balances with real on-chain data)
        resetPollingTimers();
        
        // Immediately trigger balance refresh for instant update
        const balanceWalletAddressBackend = sellWalletAddress || walletAddress || (publicKey ? publicKey.toString() : null);
        if (balanceWalletAddressBackend) {
          // Refresh SOL balance immediately
          (async () => {
            try {
              const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
              const response = await fetch(`${apiUrl}/api/wallet/balance?address=${balanceWalletAddressBackend}`);
              if (response.ok) {
                const data = await response.json();
                setBalance(data.balance || 0);
              }
            } catch (err) {
              // Silently handle errors
            }
          })();
          
          // Refresh token balance immediately
          (async () => {
            try {
              const balanceData = await getTokenBalance(balanceWalletAddressBackend, tokenAddress);
              if (!balanceData.error && balanceData.balanceUi !== undefined) {
                setTokenBalance(balanceData.balanceUi);
              }
            } catch (err) {
              // Silently handle errors
            }
          })();
        }
        
        // 🔄 BACKGROUND VERIFICATION: Confirm trade and update with real on-chain data (non-blocking)
        (async () => {
          try {
            // Confirm trade in database
            const confirmRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/trade/confirm`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                txSignature: result.signature,
                mint: tokenAddress,
                walletAddress: sellWalletAddress || walletAddress || (publicKey ? publicKey.toString() : ''),
                side: 'SELL',
                price: currentPrice || 0,
                sizeSol: expectedSolOutput,
                tokenAmount: finalTokenAmount,
                positionId: result.positionId
              })
            });

            if (confirmRes.ok) {
              // Position will be updated/closed by the backend
              await refreshPosition();
            }
            
            // Refresh balances with real on-chain data (with retries)
            await waitForBalancesToUpdate(true, true);
            
            // Aggressive SOL balance refresh (multiple retries)
            for (let retry = 0; retry < 8; retry++) {
              if (retry > 0) {
                await new Promise(resolve => setTimeout(resolve, 400 * retry));
              }
              try {
                const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
                if (!balanceWalletAddress) break;
                const solRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallet/balance?address=${balanceWalletAddress}`);
                if (solRes.ok) {
                  const solData = await solRes.json();
                  setBalance(solData.balance || 0);
                  if (retry >= 3) break;
                }
              } catch (err) {
                // Silently handle errors
              }
            }
            
            // Final balance refresh
            try {
              const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
              if (balanceWalletAddress) {
                const solRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallet/balance?address=${balanceWalletAddress}`);
              if (solRes.ok) {
                const solData = await solRes.json();
                setBalance(solData.balance || 0);
              }
                const tokenData = await getTokenBalance(balanceWalletAddress, tokenAddress);
              setTokenBalance(tokenData.balanceUi || 0);
              setTokenDecimals(tokenData.decimals || 6);
              if (tokenData.balanceUi <= 0.0001) {
                setOpenPosition(null);
                }
              }
            } catch (err) {
              // Silently handle errors
            }
          } catch (err) {
            // Log background verification errors but don't affect UI (sell already succeeded)
            logger.error('API_CALL', 'Background verification error (non-blocking)', { 
              eventId: sellEventId, 
              error: err.message,
              stack: err.stack
            });
          }
        })();
        
        return;
      }
      // Handle frontend-signed transaction
      if (result.transaction) {
        // Keep execution stage as "placing" - waiting for signature
        
        const transactionBuffer = Buffer.from(result.transaction, 'base64');
        
        // Try to detect transaction type by attempting to deserialize
        // Versioned transactions must be deserialized with VersionedTransaction.deserialize()
        // Legacy transactions use Transaction.from()
        let transaction;
        let isVersioned = false;
        
        try {
          // Try versioned first (most common for Jupiter)
          transaction = VersionedTransaction.deserialize(transactionBuffer);
          isVersioned = true;
        } catch (e) {
          // If that fails, try legacy
          try {
            transaction = Transaction.from(transactionBuffer);
            isVersioned = false;
          } catch (e2) {
            throw new Error(`Failed to deserialize transaction: ${e2.message}`);
          }
        }
        
        let signature;
        
        // Sign transaction - use vault keypair if available, otherwise Phantom wallet
        if (isVersioned) {
          // Handle versioned transaction (V0)
          if (isUnlocked && vault && vaultKey) {
            // Use vault keypair for signing
            try {
              const keypair = await getPrimaryWalletKeypair(vault, vaultKey);
              transaction.sign([keypair]);
              // Send signed transaction directly
              signature = await connection.sendTransaction(transaction, {
          skipPreflight: false,
                preflightCommitment: 'processed',
          maxRetries: 3
        });
            } catch (error) {
              console.error("Error signing versioned transaction with vault keypair:", error);
              // Fallback to Phantom wallet if vault signing fails
              if (!sendTransaction) {
                throw new Error("Vault signing failed and wallet does not support transaction signing");
              }
              signature = await sendTransaction(transaction, connection, {
                skipPreflight: false,
                maxRetries: 3
              });
            }
          } else {
            // Use Phantom wallet for signing versioned transaction
            if (!sendTransaction) {
              throw new Error("Wallet does not support transaction signing. Please connect a wallet or unlock your vault.");
            }
            signature = await sendTransaction(transaction, connection, {
              skipPreflight: false,
              maxRetries: 3
            });
          }
        } else {
          // Handle legacy transaction
          if (isUnlocked && vault && vaultKey) {
            // Use vault keypair for signing
            try {
              const keypair = await getPrimaryWalletKeypair(vault, vaultKey);
              transaction.partialSign(keypair);
              // Send signed transaction directly
              signature = await connection.sendRawTransaction(transaction.serialize(), {
                skipPreflight: false,
                preflightCommitment: 'processed',
                maxRetries: 3
              });
            } catch (error) {
              console.error("Error signing legacy transaction with vault keypair:", error);
              // Fallback to Phantom wallet if vault signing fails
              if (!sendTransaction) {
                throw new Error("Vault signing failed and wallet does not support transaction signing");
              }
              signature = await sendTransaction(transaction, connection, {
                skipPreflight: false,
                maxRetries: 3
              });
            }
          } else {
            // Use Phantom wallet for signing
            if (!sendTransaction) {
              throw new Error("Wallet does not support transaction signing. Please connect a wallet or unlock your vault.");
            }
            signature = await sendTransaction(transaction, connection, {
              skipPreflight: false,
              maxRetries: 3
            });
          }
        }
        
        const expectedOutput = result.expectedOutputAmount || "0";
        const solAmount = parseFloat(expectedOutput) / LAMPORTS_PER_SOL;
        
        // Clear timeout and show success IMMEDIATELY
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        updateExecutionStage("success");
        setButtonStatus("success");
        
        // ⚡ INSTANT UPDATE: Update SOL balance immediately when success message appears
        // Add the exact SOL amount received from the sell
        const newSolBalance = balance + solAmount;
        setBalance(newSolBalance);
        
        // Update token balance immediately (subtract tokens sold)
        const newTokenBalance = Math.max(0, currentTokenBalance - finalTokenAmount);
        setTokenBalance(newTokenBalance);
        
        // Update/close position immediately
        if (openPosition && openPosition.mint === tokenAddress) {
          const remainingTokens = Math.max(0, (parseFloat(openPosition.token_amount) || 0) - finalTokenAmount);
          const remainingSize = remainingTokens > 0.0001 ? 
            ((parseFloat(openPosition.size_sol) || 0) * (remainingTokens / (parseFloat(openPosition.token_amount) || 1))) : 0;
          
          if (remainingTokens <= 0.0001) {
            // Position fully closed
            setOpenPosition(null);
          } else {
            // Position partially closed - update amounts
            setOpenPosition({
              ...openPosition,
              token_amount: remainingTokens,
              size_sol: remainingSize
            });
          }
        } else {
          // No position to update
          setOpenPosition(null);
        }
        setAmount("");
        setIsExecuting(false);
        clearButtonStatus();
        
        // Reset polling timers
        resetPollingTimers();
        
        // 🔄 BACKGROUND VERIFICATION: Confirm transaction and update with real data (non-blocking)
        (async () => {
          try {
            // Confirm on-chain (non-blocking)
            try {
              await connection.confirmTransaction(signature, 'confirmed');
            } catch (err) {
              // Confirmation timeout - proceed anyway
            }
            
            // Confirm trade in database
            const confirmRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/trade/confirm`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                txSignature: signature,
                mint: tokenAddress,
                walletAddress: sellWalletAddress || walletAddress || (publicKey ? publicKey.toString() : ''),
                side: 'SELL',
                price: currentPrice || 0,
                sizeSol: solAmount,
                tokenAmount: finalTokenAmount,
                positionId: result.positionId
              })
            });

            if (confirmRes.ok) {
              await refreshPosition();
            }
            
            // Refresh balances with real on-chain data (with retries)
            await waitForBalancesToUpdate(true, true);
            
            // Aggressive SOL balance refresh (multiple retries)
            for (let retry = 0; retry < 8; retry++) {
              if (retry > 0) {
                await new Promise(resolve => setTimeout(resolve, 400 * retry));
              }
              try {
                const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
                if (!balanceWalletAddress) break;
                const solRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallet/balance?address=${balanceWalletAddress}`);
                if (solRes.ok) {
                  const solData = await solRes.json();
                  setBalance(solData.balance || 0);
                  if (retry >= 3) break;
                }
              } catch (err) {
                // Silently handle errors
              }
            }
            
            // Final balance refresh
            try {
              const balanceWalletAddress = walletAddress || (connected && publicKey ? publicKey.toString() : null);
              if (balanceWalletAddress) {
                const solRes = await fetch(`${process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000'}/api/wallet/balance?address=${balanceWalletAddress}`);
              if (solRes.ok) {
                const solData = await solRes.json();
                setBalance(solData.balance || 0);
              }
                const tokenData = await getTokenBalance(balanceWalletAddress, tokenAddress);
              setTokenBalance(tokenData.balanceUi || 0);
              setTokenDecimals(tokenData.decimals || 6);
              if (tokenData.balanceUi <= 0.0001) {
                setOpenPosition(null);
                }
              }
            } catch (err) {
              // Silently handle errors
            }
          } catch (err) {
            // Log background verification errors but don't affect UI (sell already succeeded)
            logger.error('API_CALL', 'Background verification error (non-blocking)', { 
              eventId: sellEventId, 
              error: err.message,
              stack: err.stack
            });
          }
        })();
      } else {
        // No valid result - show error
        if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
        const errorMsg = result?.error || "No transaction data returned";
        logger.error('TRADING', 'Sell: No transaction data returned', { 
          eventId: sellEventId, 
          result: result 
        });
        // Invalid sell result structure
        updateExecutionStage("failed");
        setErrorMessage(errorMsg);
        setButtonStatus("failed");
        setIsExecuting(false);
        clearButtonStatus();
      }
    } catch (err) {
      // Main sell exception - this is a real error
      logger.error('TRADING', 'Sell exception (main catch)', { 
        eventId: sellEventId, 
        error: err.message,
        stack: err.stack
      });
      if (executionTimeoutRef.current) clearTimeout(executionTimeoutRef.current);
      updateExecutionStage("failed");
      setErrorMessage(err.message || "Sell failed");
      setButtonStatus("failed");
      setIsExecuting(false);
      clearButtonStatus();
    }
  };

  const getButtonText = () => {
    const amountValue = parseFloat(amount) || 0;
    
    // Show execution stage if executing
      if (isExecuting && executionStage) {
      const stageMessages = {
        "placing": mode === "buy" ? "Placing Buy..." : "Placing Sell...",
        "verifying": "Verifying transaction...",
        "creating-position": "Creating position...",
        "success": "Success",
        "failed": "Failed - Retry"
      };
      return stageMessages[executionStage] || (mode === "buy" ? "Placing Buy..." : "Placing Sell...");
    }
    
    if (mode === "buy") {
      if (buttonStatus === "success") return "Success";
      if (buttonStatus === "failed") return "Failed - Retry";
      if (amountValue > 0) {
        return `Buy ${amountValue.toFixed(4)} SOL - ${tokenName}`;
      }
      return "Buy";
    } else {
      if (buttonStatus === "success") return "Success";
      if (buttonStatus === "failed") return "Failed - Retry";
      if (amountValue > 0) {
        return `Sell ${formatAmount(amountValue)} - ${tokenName}`;
      }
      return "Sell";
    }
  };

  const getButtonClassName = () => {
    const base = "w-full px-4 py-3 font-medium rounded-lg transition-colors";
    if (mode === "buy") {
      if (buttonStatus === "success") {
        return `${base} bg-green-600 text-white`;
      } else if (buttonStatus === "failed") {
        return `${base} bg-red-600 text-white`;
      } else {
        return `${base} bg-green-500 hover:bg-green-600 text-white cursor-pointer`;
      }
    } else {
      if (buttonStatus === "success") {
        return `${base} bg-green-600 text-white`;
      } else if (buttonStatus === "failed") {
        return `${base} bg-red-600 text-white`;
      } else {
        return `${base} bg-pink-500 hover:bg-pink-600 text-white cursor-pointer`;
      }
    }
  };

  return (
    <div className="border-t border-white/20 pt-4 mt-4">
      {!walletAddress ? (
        <div className="text-center py-4">
          <p className="text-sm text-white/60 mb-2">Connect wallet to trade</p>
        </div>
      ) : (
        <>
          {/* Mode Toggle */}
          <div className="mb-4 flex gap-2 p-1 bg-white/5 rounded-lg">
            <button
              onClick={() => {
                setMode("buy");
                setAmount("");
                setButtonStatus(null);
                setErrorMessage(null);
                updateExecutionStage(null);
              }}
              className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                mode === "buy"
                  ? "bg-green-500 text-white"
                  : "bg-transparent text-white/60 hover:text-white"
              }`}
            >
              Buy
            </button>
            <button
              onClick={() => {
                setMode("sell");
                setAmount("");
                setButtonStatus(null);
                setErrorMessage(null);
                updateExecutionStage(null);
              }}
              className={`flex-1 px-4 py-2 rounded text-sm font-medium transition-colors ${
                mode === "sell"
                  ? "bg-pink-500 text-white"
                  : "bg-transparent text-white/60 hover:text-white"
              }`}
            >
              Sell
            </button>
          </div>

          {/* Settings Toggle */}
          <div className="mb-4">
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="w-full px-3 py-2 text-xs bg-white/5 hover:bg-white/10 text-white/60 rounded transition-colors flex items-center justify-between"
            >
              <span>⚙️ Trading Settings ({mode === "buy" ? "Buy" : "Sell"})</span>
              <span>{showSettings ? "▼" : "▶"}</span>
            </button>
            
            {showSettings && (
              <div className="mt-2 p-3 bg-white/5 rounded-lg space-y-3">
                <div>
                  <label className="block text-xs text-white/60 mb-1">
                    Slippage (%)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={mode === "buy" ? buySettings.slippage : sellSettings.slippage}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (mode === "buy") {
                        setBuySettings({ ...buySettings, slippage: value });
                      } else {
                        setSellSettings({ ...sellSettings, slippage: value });
                      }
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">
                    Priority Fee (SOL)
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={mode === "buy" ? buySettings.priorityFee : sellSettings.priorityFee}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (mode === "buy") {
                        setBuySettings({ ...buySettings, priorityFee: value });
                      } else {
                        setSellSettings({ ...sellSettings, priorityFee: value });
                      }
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-white/60 mb-1">
                    Tip (SOL)
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={mode === "buy" ? buySettings.tip : sellSettings.tip}
                    onChange={(e) => {
                      const value = parseFloat(e.target.value) || 0;
                      if (mode === "buy") {
                        setBuySettings({ ...buySettings, tip: value });
                      } else {
                        setSellSettings({ ...sellSettings, tip: value });
                      }
                    }}
                    className="w-full px-3 py-2 bg-white/5 border border-white/20 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-white/30"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Quiet Balance Display (no loading states) */}
          <div className="mb-4 p-3 bg-white/5 rounded-lg">
            <div className="flex justify-between items-center text-sm">
              <span className="text-white/60">SOL Balance:</span>
              <span className="text-white font-medium">
                {balance.toFixed(4)} SOL
              </span>
            </div>
            {tokenBalance > 0.0001 && (
              <div className="flex justify-between items-center text-sm mt-2">
                <span className="text-white/60">Token Balance:</span>
                <span className="text-white font-medium">
                  {tokenBalance.toFixed(4)}
                </span>
              </div>
            )}
          </div>

          {/* Amount Input */}
          <div className="mb-4">
            <input
              type="number"
              value={amount}
              onChange={(e) => {
                setAmount(e.target.value);
                // Trigger prefetch when user types in buy mode
                if (mode === "buy" && e.target.value && parseFloat(e.target.value) > 0) {
                  triggerPrefetch();
                }
              }}
              placeholder={mode === "buy" ? "Amount in SOL" : "Amount in tokens"}
              className="w-full px-4 py-2 bg-white/5 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-white/30"
              disabled={isExecuting}
            />
            {/* Percentage Buttons */}
            <div className="flex gap-2 mt-2">
              {[0.25, 0.5, 0.75, 1.0].map((percent) => (
                <button
                  key={percent}
                  onClick={() => handlePercentageClick(percent)}
                  className="flex-1 px-2 py-1 text-xs bg-white/5 hover:bg-white/10 text-white/80 rounded transition-colors"
                  disabled={isExecuting || (mode === "sell" && tokenBalance <= 0)}
                >
                  {percent * 100}%
                </button>
              ))}
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={mode === "buy" ? handleBuy : handleSell}
            onMouseEnter={mode === "buy" ? triggerPrefetch : undefined}
            className={getButtonClassName()}
          >
            {getButtonText()}
          </button>

          {/* Error Message (subtle) */}
          {errorMessage && (
            <div className="mt-2 text-xs text-center text-red-400">
              {errorMessage}
            </div>
          )}

          {/* Open Position Info - Only show if we actually have tokens */}
          {openPosition && tokenBalance > 0.0001 && (
            <div className="mt-4 p-3 bg-white/5 rounded-lg">
              <div className="text-xs text-white/60 mb-2">Open Position</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/60">Entry Price:</span>
                  <span className="text-white">{parseFloat(openPosition.entry_price || 0).toFixed(8)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Current Price:</span>
                  <span className="text-white">{currentPrice ? currentPrice.toFixed(8) : "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">PnL:</span>
                  <span className={livePnL.pnlPct >= 0 ? "text-green-400" : "text-red-400"}>
                    {livePnL.pnlPct.toFixed(2)}% ({livePnL.pnlSol.toFixed(4)} SOL)
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/60">Total Position:</span>
                  <span className="text-white">
                    {livePnL.totalPosition.toFixed(4)} SOL
                  </span>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TradingControls;
